import React, { useState } from "react";
import { useRequest } from "ahooks";
import { useHistory, useParams } from "react-router-dom";
import { getManufacturers, getOrderApi, updateOrderApi } from "../../api";
import AccountLayout from "../../layouts/AccountLayout";
import PositionItem from "./PositionItem";
import {
  ApprovalStatus,
  ChooseUploadType,
  ImageFile,
  Manufacturer,
  PositionImage,
  StatusConst,
  UserOrder,
} from "../../interfaces";
import DownloadImages from "./DownloadImages";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ArrowIcon, CheckGreenIcon } from "../../assets/svgs";
import Attachments from "./Attachments";
import { Button, Checkbox, Loader, Textarea } from "@mantine/core";
import useOrderPosition from "../../hooks/useOrderPosition";
import ApproveDeclineAll from "./ViewOrder/ApproveDeclineAll";
import DeclineAllModal from "./ViewOrder/DeclineAllModal";
import AcceptAllModal from "./ViewOrder/AcceptAllModal";
import ViewOrderHeader from "./ViewOrderHeader";
import OrderAddressCard from "../account/OrderAddressCard";
import { VIEW_ORDER_COLUMNS } from "../../constant/ViewOrderConstant";
import useOrderColumn from "../../hooks/useOrderColumn";
import OrderTable from "./OrderTable";
import Notes from "./ViewOrder/Notes";
import { useUser } from "../../contexts";
import ActivityLogs from "./ActivityLogs";
import useSubscriber from "../../hooks/useSubscriber";
// @ts-expect-error type exists
import { onUpdateOrderItem } from "../../graphql/subscriptions";

const Order: React.FC = () => {
  const { id }: { id: string } = useParams();
  const history = useHistory();
  const { t } = useTranslation();
  const { data: updatedOrderSubscribe } = useSubscriber(onUpdateOrderItem);
  const { data: manufacturers } = useRequest<Manufacturer[]>(() =>
    getManufacturers()
  );
  const [{ user }] = useUser();
  const [approved, setApproved] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);
  const { data, loading, mutate } = useRequest<UserOrder>(
    () => getOrderApi(id),
    {
      ready: !!id,
      onSuccess: (res) => {
        if (!res) {
          toast.error(t("orderNotFound"));
          history.push("/dashboard");
        }
      },
    }
  );
  const updateOrder = useRequest(updateOrderApi, {
    manual: true,
  });
  const [approveModalVisible, setApproveModalVisible] =
    useState<boolean>(false);
  const [declineModalVisible, setDeclineModalVisible] =
    useState<boolean>(false);
  const { positionImageAvailable, declineAllImage, approveAllImage } =
    useOrderPosition(data);

  const onApproveModalOpen = () => {
    setApproveModalVisible(true);
  };

  const onApproveModalClose = () => {
    setApproveModalVisible(false);
  };

  const onDeclineModalOpen = () => {
    setDeclineModalVisible(true);
  };

  const onDeclineModalClose = () => {
    setDeclineModalVisible(false);
  };

  const onDeclineAllImages = async (
    rejectedDescription: string,
    rejectedReason: string,
    files: ImageFile[]
  ) => {
    setDeclineModalVisible(false);
    const updatedOrder = await declineAllImage(
      rejectedDescription,
      rejectedReason,
      files
    );
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("declineAllPrintData"));
  };

  const onApproveAllImages = async (acceptedDescription: string) => {
    setApproveModalVisible(false);
    const updatedOrder = await approveAllImage(acceptedDescription);
    if (updatedOrder) {
      mutate(updatedOrder);
    }
    toast.success(t("acceptAllPrintData"));
  };

  const sendPrintDataProduction = async () => {
    if (!data) return;

    for (let i = 0; i < data.positions.length; i++) {
      const position = data.positions[i];
      const sum = position.positionImage?.reduce(
        (acc: number, curr: PositionImage) => acc + curr.quantity,
        0
      );

      if (sum !== position.quantity && !position.White) {
        toast.error(t("positionsNotCompleted"));
        return;
      }

      if (
        position?.productGroup &&
        position.positionImage?.find(
          (image) => image.approvalStatus === ApprovalStatus.No
        )
      ) {
        toast.error(t("positionsNotCompleted"));
        return;
      }
    }

    await updateOrder.run({
      id,
      status: StatusConst.Production,
      uploadedDate: new Date().toISOString(),
    });
    setTimeout(() => setSent(true), 300);
  };

  const { filterColumns } = useOrderColumn({
    orders: [data as UserOrder],
    orderFilter: null,
    manufacturers: manufacturers ?? [],
    onUpdateOrder: (order) => mutate(order),
  });

  const handleOrderRefresh = (order: Partial<UserOrder> & { id: string }) => {
    if (order.id !== data?.id || order.chooseUpload === data.chooseUpload)
      return;
    if (order.chooseUpload === ChooseUploadType.bulkUpload) {
      history.push(`/dashboard/vieworder/${order.id}`);
    }
  };

  React.useEffect(() => {
    if (!user) return;
    updateOrder.run({
      id,
      lastOpened: user.email,
    });
  }, [user]);

  React.useEffect(() => {
    if (
      !updatedOrderSubscribe ||
      (updatedOrderSubscribe &&
        !(updatedOrderSubscribe as Record<"onUpdateOrderItem", UserOrder>)
          .onUpdateOrderItem)
    )
      return;

    handleOrderRefresh(
      (updatedOrderSubscribe as Record<"onUpdateOrderItem", UserOrder>)
        .onUpdateOrderItem
    );
  }, [updatedOrderSubscribe]);

  return (
    <AccountLayout account>
      {!sent ? (
        <>
          <ViewOrderHeader orderId={id} />
          {!loading && data ? (
            <>
              <div className="grid gap-30 print-data-upload-admin pt-20">
                <div>
                  <OrderAddressCard
                    isShippingAddressEditable={true}
                    isBillingAddressEditable={true}
                    data={data}
                    onUpdateOrder={(order) => mutate(order)}
                  />
                </div>
                <Notes order={data} mutate={mutate} />
              </div>
              <div className="py-40 pl-8 pr-8">
                <OrderTable
                  isPagination={false}
                  columns={filterColumns(VIEW_ORDER_COLUMNS)}
                  data={[data]}
                  nextToken={null}
                  loadOrders={() => {}}
                  fetchLoading={loading}
                />
              </div>
              <div className="grid gap-30 print-data-changelog">
                <div className="">
                  <p className="section-heading ">{t("positions")}</p>

                  <div className="position-items">
                    <div className="position-item__header sticky hide-md">
                      <div className="header-item">
                        <h2>{t("product")}</h2>
                        <h2>{t("productNumber")}</h2>
                      </div>
                      <div className="header-item">
                        <h2>{t("dimension")}</h2>
                      </div>
                      <div className="header-item">
                        <h2>{t("quantity")}</h2>
                      </div>
                      <div className="header-item">
                        <h2>{t("remaining")}</h2>
                      </div>
                      <div className="header-item not-visible">
                        <ArrowIcon hide />
                      </div>
                    </div>
                    {data?.positions?.map((position, index) => (
                        <div>
                            <PositionItem
                                key={index}
                                order={data}
                                item={position}
                                index={index}
                                mutate={mutate}
                            />

                            { position.positionImage?.map((image, index) => (
                                <div className="mt-3 card">
                                    <p>{ index + 1 }.</p>
                                    <p>{ image.quantity }x { t("fileName") }: { image.pdfKey }</p>
                                    <p>{ t("size") }: { (image.size! / 1000000).toFixed(2) } MB</p>
                                </div>
                            )) }
                        </div>
                    ))}
                  </div>
                  {positionImageAvailable(data) && (
                <div className="mt-10">
                  <ApproveDeclineAll
                    onApproveModalOpen={onApproveModalOpen}
                    onDeclineModalOpen={onDeclineModalOpen}
                  />
                  <div className="mb-70">
                    <DownloadImages positions={data?.positions} />
                  </div>

                  <p className="section-heading">{t("attachments")}</p>
                  <Attachments files={data?.files || []} />
                </div>
              )}
                </div>
                <div className="print-data-changelog-width">
                  <ActivityLogs orderId={data.id} />
                </div>
              </div>

              <div className="w-sx"></div>

              <p className="section-heading mt-70">{t("furtherInformation")}</p>
              <Checkbox
                radius={5}
                disabled
                label={t("furtherInfoFlowingMotifs")}
                className="my-10"
                checked={data.furtherInfoFlowingMotifs ?? false}
              />
              <Checkbox
                radius={5}
                disabled
                label={t("furtherInfoQualityCheck")}
                className="mb-20"
                checked={data.furtherInfoQualityCheck ?? false}
              />
              <div className="w-sx event-none">
                <Textarea
                  className="mt-10 mb-30"
                  minRows={4}
                  defaultValue={data.furtherInfo}
                ></Textarea>
              </div>
              <div className="send-printdata mt-70">
                <Checkbox
                  checked={approved}
                  onChange={(e: any) => setApproved(e.target.checked)}
                  label={t("approveText")}
                />
                <Button
                  mt={10}
                  size="lg"
                  loading={updateOrder.loading}
                  disabled={!approved || updateOrder.loading}
                  onClick={() => sendPrintDataProduction()}
                >
                  {t("sendToProduction")}
                </Button>
              </div>
            </>
          ) : (
            <div className="row row-center mt-30">
              <Loader />
            </div>
          )}
        </>
      ) : (
        <div className="sent-printdata">
          <CheckGreenIcon />
          <span>{t("sendSuccessProduction")}</span>
        </div>
      )}
      {declineModalVisible && (
        <DeclineAllModal
          modalVisible={declineModalVisible}
          setModalVisible={setDeclineModalVisible}
          onDanger={onDeclineModalClose}
          onSuccess={(
            rejectedDescription: string,
            rejectedReason: string,
            files: ImageFile[]
          ) => onDeclineAllImages(rejectedDescription, rejectedReason, files)}
          title={"declineAllPrintDataWarning"}
          contentStyle={"pb-16"}
        />
      )}

      {approveModalVisible && (
        <AcceptAllModal
          modalVisible={approveModalVisible}
          setModalVisible={setApproveModalVisible}
          onDanger={onApproveModalClose}
          onSuccess={(acceptedDescription: string) =>
            onApproveAllImages(acceptedDescription)
          }
          title={"acceptAllPrintDataWarning"}
          contentStyle={"pb-16"}
        />
      )}
    </AccountLayout>
  );
};

export default Order;
